import Breadcrumbs from 'Components/Breadcrumbs'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteButton from 'Components/DeleteButton'
import PageTitle from 'Components/PageTitle'
import { WithLoader } from 'Components/WithLoader'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import TariffDetails from 'Services/ProductManagement/Features/Tariff/Components/TariffDetails'
import { TariffContext } from 'Services/ProductManagement/Features/Tariff/Context/tariffContext'
import { useSelectedTariff } from 'Services/ProductManagement/Features/Tariff/Hooks'
import {
  useCopySingleTariffMutation,
  useDeleteSingleTariffMutation
} from 'Services/ProductManagement/Features/Tariff/tariffApi'
import { type Tariff, TariffStatus } from 'Services/ProductManagement/Features/Tariff/types'
import useTypedSelector from 'Store'
import { IS_TARIFF_EDIT_ALLOWED, PRODUCT_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import ContentCopy from '@mui/icons-material/ContentCopy'
import { Box, Button, Grid } from '@mui/material'
import { useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

export default function TariffDetailPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const { id: tariffId } = useParams()
  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const { setSelectedTariffId } = useContext(TariffContext)
  const { user } = useTypedSelector(state => state.user)

  const { singleTariff, isSingleTariffLoading } = useSelectedTariff(tariffId)
  const [copySingleTariff, { isLoading: isCopyLoading }] = useCopySingleTariffMutation()
  const [deleteSingleTariff, { isLoading: isDeleteLoading }] = useDeleteSingleTariffMutation()

  const tariffTitle = singleTariff ? singleTariff.description : ''
  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Tariffs`,
      link: baseUrl
    },
    {
      title: tariffTitle
    },
    {
      title: t`Tariff details`
    }
  ]

  const handleCopiedTariffSuccess = (copiedTariff: Tariff) => {
    navigate(`${baseUrl}/${copiedTariff.id}/products`)
    setSelectedTariffId(copiedTariff.id)
  }

  const copySelectedTariff = (tariffId: string) => {
    copySingleTariff({ id: tariffId })
      .unwrap()
      .then((tariffResponse: Tariff) => {
        handleCopiedTariffSuccess(tariffResponse)
        SnackbarUtils.success(t`Tariff was successfully copied.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error copying the tariff.`)
      })
  }

  const deleteSelectedTariff = (tariffId: string) => {
    deleteSingleTariff(tariffId)
      .unwrap()
      .then(() => {
        navigate(baseUrl)
        SnackbarUtils.success(t`Tariff was successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the tariff.`)
      })
  }

  /**
   * Superuser can delete tariff in any status. Authority admin can only delete tariff in DRAFT or STAGED status.
   */
  const isTariffDeleteAllowed =
    singleTariff &&
    (isSuperuser(user) || [TariffStatus.DRAFT, TariffStatus.STAGED].includes(singleTariff.status))

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Breadcrumbs items={breadcrumbs} />
          <PageTitle>{t`Tariff details`}</PageTitle>
          <Box display="flex" justifyContent="end" columnGap={2}>
            {IS_TARIFF_EDIT_ALLOWED && isTariffDeleteAllowed && (
              <DeleteButton
                size="medium"
                loading={isDeleteLoading}
                loadingPosition="end"
                onClick={() =>
                  confirm({
                    title: t`Are you sure?`,
                    message: t`Are you sure you want to delete the tariff?`,
                    handleConfirm: () => {
                      deleteSelectedTariff(singleTariff.id)
                    }
                  })
                }
              >{t`Delete tariff`}</DeleteButton>
            )}
            {singleTariff && IS_TARIFF_EDIT_ALLOWED && (
              <CustomLoadingButton
                size="medium"
                isLoading={isCopyLoading}
                icon={<ContentCopy />}
                iconPosition="start"
                onClick={() =>
                  confirm({
                    title: t`Are you sure?`,
                    message: t`Are you sure you want to make a copy of ${tariffTitle}?`,
                    handleConfirm: () => {
                      copySelectedTariff(singleTariff.id)
                    }
                  })
                }
              >{t`Copy tariff`}</CustomLoadingButton>
            )}
            {IS_TARIFF_EDIT_ALLOWED && (
              <Button
                variant="outlined"
                size="medium"
                color="inherit"
                onClick={() => navigate(`${baseUrl}/${tariffId}/edit`)}
              >
                {t`Edit tariff`}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <WithLoader loading={isSingleTariffLoading}>
            {singleTariff && <TariffDetails tariff={singleTariff} />}
          </WithLoader>
        </Grid>
      </Grid>
    </Box>
  )
}
